/**
 * Google reCAPTCHA v3 — lazy-loaded, invisible. Returns a token for a given
 * action to send with sensitive requests (checkout, signup). When no site key
 * is configured (VITE_RECAPTCHA_SITE_KEY), it no-ops and returns undefined, and
 * the backend treats verification as disabled.
 */
const SITE_KEY =
  (import.meta.env as Record<string, string | undefined>).VITE_RECAPTCHA_SITE_KEY || undefined;

export const recaptchaEnabled = !!SITE_KEY;

let loadPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (!SITE_KEY) return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-recaptcha]');
    if (existing) return resolve();
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    s.async = true;
    s.defer = true;
    s.dataset.recaptcha = '1';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('reCAPTCHA failed to load'));
    document.head.appendChild(s);
  });
  return loadPromise;
}

/** Get a reCAPTCHA token for an action, or undefined when not configured/failed. */
export async function getRecaptchaToken(action: string): Promise<string | undefined> {
  if (!SITE_KEY) return undefined;
  try {
    await loadScript();
    const grecaptcha = (window as unknown as { grecaptcha?: { ready: (cb: () => void) => void; execute: (k: string, o: { action: string }) => Promise<string> } }).grecaptcha;
    if (!grecaptcha) return undefined;
    await new Promise<void>((r) => grecaptcha.ready(r));
    return await grecaptcha.execute(SITE_KEY, { action });
  } catch {
    return undefined;
  }
}
